import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Dashboard languages (Settings › Language). The English text is the key, so anything not translated yet simply
 * shows in English. Translated: the dashboard shell (menu, top bar, profile menu), Settings and Help & Support.
 * Each language is a separate file, downloaded only when someone chooses it.
 */
export const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'te', name: 'Telugu', native: 'తెలుగు' },
  { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
];
const LOADERS = {
  ta: () => import('./locales/ta'),
  hi: () => import('./locales/hi'),
  te: () => import('./locales/te'),
  kn: () => import('./locales/kn'),
};
const loaded = {};

/** Fetches a language's file (once); resolves when translate() can use it. */
export function loadLanguage(lang) {
  if (!LOADERS[lang] || loaded[lang]) return Promise.resolve(loaded[lang] || null);
  return LOADERS[lang]().then((m) => { loaded[lang] = m.default; return m.default; }).catch(() => null);
}

/** "Hello {name}" with {name: 'Priya'} → "Hello Priya". */
const fill = (text, vars) => (vars ? text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m)) : text);

export function translate(lang, text, vars) {
  const dict = loaded[lang];
  return fill((dict && dict[text]) || text, vars);
}

const I18nContext = createContext({ lang: 'en', t: (text, vars) => fill(text, vars) });

export function I18nProvider({ lang = 'en', children }) {
  const [ready, setReady] = useState(() => (loaded[lang] ? lang : 'en'));
  useEffect(() => {
    let live = true;
    loadLanguage(lang).then(() => { if (live) setReady(lang); });
    return () => { live = false; };
  }, [lang]);
  const t = useCallback((text, vars) => translate(ready, text, vars), [ready]);
  const value = useMemo(() => ({ lang: ready, t }), [ready, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** { lang, t }: t('Save Changes') in the person's language. */
export const useT = () => useContext(I18nContext);
